import React, { useState, useEffect, useCallback } from 'react';
import AdminHeader from 'components/navigation/AdminHeader';
import AdminNavigation from 'components/navigation/AdminNavigation';
import Icon from 'components/AppIcon';
import { supabase } from '../../lib/supabase';
import RecipientFormModal from './components/RecipientFormModal';
import DeleteConfirmModal from './components/DeleteConfirmModal';

const NotificationRecipients = () => {
  const [recipients, setRecipients] = useState([]);
  const [suppressions, setSuppressions] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [formModal, setFormModal] = useState({ open: false, recipient: null });
  const [deleteModal, setDeleteModal] = useState({ open: false, recipient: null });
  const [toastMsg, setToastMsg] = useState(null);

  const showToast = (msg, type = 'success') => {
    setToastMsg({ msg, type });
    setTimeout(() => setToastMsg(null), 3500);
  };

  const fetchSuppressions = useCallback(async () => {
    try {
      const { data } = await supabase
        ?.from('email_suppressions')
        ?.select('email, reason, bounce_type, first_seen_at')
        ?.eq('suppressed', true);
      const map = {};
      (data ?? [])?.forEach(row => {
        map[row.email.toLowerCase().trim()] = row;
      });
      setSuppressions(map);
    } catch {
      // non-fatal — suppression data is best-effort for UI display
    }
  }, []);

  const fetchRecipients = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: fetchErr } = await supabase?.from('notification_recipients')?.select('*')?.order('created_at', { ascending: true });

      if (fetchErr) throw fetchErr;
      setRecipients(data ?? []);
    } catch (err) {
      setError(err?.message ?? 'Failed to load recipients.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRecipients();
    fetchSuppressions();
  }, [fetchRecipients, fetchSuppressions]);

  const handleSave = async (formData, editingId) => {
    try {
      if (editingId) {
        const { error: updateErr } = await supabase?.from('notification_recipients')?.update({
            name: formData?.name,
            email: formData?.email || null,
            phone: formData?.phone || null,
            receives_email: formData?.receives_email,
            receives_sms: formData?.receives_sms,
            active: formData?.active,
          })?.eq('id', editingId);
        if (updateErr) throw updateErr;
        showToast('Recipient updated successfully.');
      } else {
        const { error: insertErr } = await supabase?.from('notification_recipients')?.insert({
            name: formData?.name,
            email: formData?.email || null,
            phone: formData?.phone || null,
            receives_email: formData?.receives_email,
            receives_sms: formData?.receives_sms,
            active: formData?.active,
          });
        if (insertErr) throw insertErr;
        showToast('Recipient added successfully.');
      }
      setFormModal({ open: false, recipient: null });
      fetchRecipients();
      fetchSuppressions();
    } catch (err) {
      throw err;
    }
  };

  const handleToggleActive = async (recipient, type) => {
    // Prevent re-enabling email alerts for a suppressed address
    if (type === 'receives_email' && !recipient?.receives_email) {
      const key = recipient?.email?.toLowerCase()?.trim();
      if (key && suppressions?.[key]) {
        showToast('Cannot re-enable email alerts while the address is bouncing. Fix the address first.', 'error');
        return;
      }
    }
    try {
      const { error: updateErr } = await supabase?.from('notification_recipients')?.update({ [type]: !recipient?.[type] })?.eq('id', recipient?.id);
      if (updateErr) throw updateErr;
      showToast(`${recipient?.name} ${!recipient?.[type] ? 'enabled' : 'disabled'} ${type}.`);
      fetchRecipients();
    } catch (err) {
      showToast(err?.message ?? `Failed to ${type === 'receives_email' ? 'disable email alerts' : 'disable text alerts'}.`, 'error');
    }
  };

  const handleDelete = async (recipient) => {
    try {
      const { error: deleteErr } = await supabase?.from('notification_recipients')?.delete()?.eq('id', recipient?.id);
      if (deleteErr) throw deleteErr;
      showToast(`${recipient?.name} deleted.`);
      setDeleteModal({ open: false, recipient: null });
      fetchRecipients();
    } catch (err) {
      showToast(err?.message ?? 'Failed to delete recipient.', 'error');
    }
  };

  const formatSuppressionReason = (row) => {
    if (!row) return '';
    const reasonLabel = row?.reason === 'hard_bounce' ? 'Hard bounce'
      : row?.reason === 'soft_bounce' ? 'Soft bounce (3+ failures)'
      : row?.reason === 'complaint' ? 'Spam complaint'
      : row?.reason;
    const date = row?.first_seen_at
      ? new Date(row.first_seen_at)?.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
      : null;
    return `${reasonLabel}${date ? ` · since ${date}` : ''}`;
  };

  return (
    <div className="min-h-screen bg-background">
      <AdminHeader />
      <AdminNavigation />
      <main className="container mx-auto px-4 sm:px-6 lg:px-8 py-8 mt-[7.5rem]">
        {/* Page Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-semibold text-foreground">RPM Personnel</h1>
            <p className="text-sm text-muted-foreground mt-1">
              RPM staff who receive email and SMS alerts when service requests are submitted.
            </p>
          </div>
          <button
            onClick={() => setFormModal({ open: true, recipient: null })}
            className="flex items-center space-x-2 px-4 py-2 bg-primary text-primary-foreground text-sm font-medium rounded-md hover:bg-primary/90 transition-colors"
          >
            <Icon name="Plus" size={16} strokeWidth={2} />
            <span>Add Person</span>
          </button>
        </div>

        {/* Toast */}
        {toastMsg && (
          <div
            className={`fixed top-4 right-4 z-[500] flex items-center space-x-2 px-4 py-3 rounded-md shadow-lg text-sm font-medium transition-all ${
              toastMsg?.type === 'error' ?'bg-destructive text-destructive-foreground' :'bg-green-600 text-white'
            }`}
          >
            <Icon name={toastMsg?.type === 'error' ? 'AlertCircle' : 'CheckCircle'} size={16} strokeWidth={2} />
            <span>{toastMsg?.msg}</span>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="flex items-center space-x-3 p-4 bg-destructive/10 border border-destructive/20 rounded-md mb-6">
            <Icon name="AlertCircle" size={18} className="text-destructive flex-shrink-0" strokeWidth={2} />
            <p className="text-sm text-destructive">{error}</p>
            <button
              onClick={fetchRecipients}
              className="ml-auto text-xs text-destructive underline hover:no-underline"
            >
              Retry
            </button>
          </div>
        )}

        {/* Table */}
        <div className="bg-card border border-border rounded-lg overflow-hidden shadow-sm">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <div className="flex items-center space-x-3 text-muted-foreground">
                <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                <span className="text-sm">Loading recipients…</span>
              </div>
            </div>
          ) : recipients?.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <div className="w-12 h-12 bg-muted rounded-full flex items-center justify-center mb-3">
                <Icon name="Bell" size={22} className="text-muted-foreground" strokeWidth={1.5} />
              </div>
              <p className="text-sm font-medium text-foreground">No recipients yet</p>
              <p className="text-xs text-muted-foreground mt-1">Add a recipient to start receiving notifications.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border bg-muted/30">
                    <th className="text-left px-4 py-3 font-medium text-muted-foreground">Name</th>
                    <th className="text-left px-4 py-3 font-medium text-muted-foreground">Email</th>
                    <th className="text-left px-4 py-3 font-medium text-muted-foreground">Phone</th>
                    <th className="text-center px-4 py-3 font-medium text-muted-foreground">Email Alerts</th>
                    <th className="text-center px-4 py-3 font-medium text-muted-foreground">Text Alerts</th>
                    <th className="text-right px-4 py-3 font-medium text-muted-foreground">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {recipients?.map((r, idx) => {
                    const emailKey = r?.email?.toLowerCase()?.trim();
                    const suppression = emailKey ? suppressions?.[emailKey] : null;
                    const isBouncing = !!suppression;

                    return (
                      <tr
                        key={r?.id}
                        className={`border-b border-border last:border-0 ${idx % 2 === 0 ? '' : 'bg-muted/10'} hover:bg-muted/20 transition-colors`}
                      >
                        <td className="px-4 py-3 font-medium text-foreground">{r?.name}</td>
                        <td className="px-4 py-3">
                          {r?.email ? (
                            <div className="flex flex-col gap-1">
                              <span className="text-muted-foreground">{r?.email}</span>
                              {isBouncing && (
                                <span
                                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-semibold bg-destructive/10 text-destructive border border-destructive/20 w-fit"
                                  title={formatSuppressionReason(suppression)}
                                >
                                  <Icon name="AlertTriangle" size={11} strokeWidth={2.5} />
                                  Email bouncing
                                  <span className="font-normal text-destructive/70 ml-1">· {formatSuppressionReason(suppression)}</span>
                                </span>
                              )}
                            </div>
                          ) : (
                            <span className="text-muted-foreground/50 italic">—</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-muted-foreground">{r?.phone ?? <span className="text-muted-foreground/50 italic">—</span>}</td>
                        <td className="px-4 py-3 text-center">
                          {isBouncing ? (
                            <span
                              title="Email alerts were automatically disabled because this address is bouncing. Fix the address to re-enable."
                              className="relative inline-flex h-5 w-9 items-center rounded-full bg-muted cursor-not-allowed opacity-60"
                            >
                              <span className="inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow translate-x-1" />
                            </span>
                          ) : (
                            <button
                              onClick={() => handleToggleActive(r, 'receives_email')}
                              title={r?.receives_email ? 'Disable email alerts' : 'Enable email alerts'}
                              className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none ${r?.receives_email ? 'bg-primary' : 'bg-muted'}`}
                            >
                              <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform ${r?.receives_email ? 'translate-x-4' : 'translate-x-1'}`} />
                            </button>
                          )}
                        </td>
                        <td className="px-4 py-3 text-center">
                          <button
                            onClick={() => handleToggleActive(r, 'receives_sms')}
                            title={r?.receives_sms ? 'Disable text alerts' : 'Enable text alerts'}
                            className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none ${r?.receives_sms ? 'bg-primary' : 'bg-muted'}`}
                          >
                            <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform ${r?.receives_sms ? 'translate-x-4' : 'translate-x-1'}`} />
                          </button>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <div className="flex items-center justify-end space-x-1">
                            <button
                              onClick={() => setFormModal({ open: true, recipient: r })}
                              className="p-1.5 text-muted-foreground hover:text-foreground hover:bg-muted rounded transition-colors"
                              title="Edit"
                            >
                              <Icon name="Pencil" size={15} strokeWidth={2} />
                            </button>
                            <button
                              onClick={() => setDeleteModal({ open: true, recipient: r })}
                              className="p-1.5 text-muted-foreground hover:text-destructive hover:bg-destructive/10 rounded transition-colors"
                              title="Delete"
                            >
                              <Icon name="Trash2" size={15} strokeWidth={2} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Summary row */}
        {!loading && recipients?.length > 0 && (
          <p className="text-xs text-muted-foreground mt-3">
            {recipients?.filter(r => r?.active)?.length} active of {recipients?.length} total recipient{recipients?.length !== 1 ? 's' : ''}
          </p>
        )}
      </main>
      {/* Modals */}
      {formModal?.open && (
        <RecipientFormModal
          recipient={formModal?.recipient}
          onSave={handleSave}
          onClose={() => setFormModal({ open: false, recipient: null })}
        />
      )}
      {deleteModal?.open && (
        <DeleteConfirmModal
          recipient={deleteModal?.recipient}
          onConfirm={() => handleDelete(deleteModal?.recipient)}
          onClose={() => setDeleteModal({ open: false, recipient: null })}
        />
      )}
    </div>
  );
};

export default NotificationRecipients;
